import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { addToast, Button, Divider, Radio, RadioGroup } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useInviteMember } from '~/hooks/useMembers';
import { useTenant } from '~/hooks/useTenant';

import { Input } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '~/components/Modal';

/**
 * Zod schema for invitation form.
 * Only admin or member roles allowed (not owner).
 */
const inviteSchema = z.object({
  email: z.email('Please enter a valid email address'),
  role: z.enum(['admin', 'member'], {
    message: 'Please select a role',
  }),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InviteMemberModal({ isOpen, onClose }: InviteMemberModalProps): JSX.Element {
  const { currentOrg } = useTenant();
  const inviteMember = useInviteMember();

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      role: 'member',
    },
  });

  const selectedRole = watch('role');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = (data: InviteFormData): void => {
    if (!currentOrg) return;

    inviteMember.mutate(
      {
        orgId: currentOrg.id,
        email: data.email,
        role: data.role,
      },
      {
        onSuccess: () => {
          addToast({
            description: `Invitation sent to ${data.email}`,
            color: 'success',
          });
          onClose();
        },
        onError: (error: Error) => {
          // Handle 409 Conflict (already member or pending invitation)
          if (error.message.includes('already') || error.message.includes('409')) {
            setError('email', {
              type: 'manual',
              message: 'This user is already a member or has a pending invitation.',
            });
          } else if (error.message.includes('400') || error.message.includes('invalid')) {
            setError('email', {
              type: 'manual',
              message: 'Please enter a valid email address.',
            });
          } else if (error.message.includes('403')) {
            addToast({
              description: 'You do not have permission to invite members.',
              color: 'danger',
            });
          } else {
            addToast({
              description: error.message || 'Failed to send invitation.',
              color: 'danger',
            });
          }
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!inviteMember.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      maxWidth="md"
      preventClosing={inviteMember.isPending}
    >
      <ModalContent>
        <form noValidate onSubmit={e => void handleSubmit(onSubmit)(e)}>
          <ModalHeader>Invite Member</ModalHeader>
          <Divider />
          <ModalBody>
            <Input
              id="invite-email"
              labelPlacement="outside-top"
              label="Email Address"
              placeholder="colleague@example.com"
              type="email"
              description="Enter the email address of the person you want to invite"
              isRequired
              isInvalid={!!errors.email}
              errorMessage={errors.email?.message}
              {...register('email')}
            />

            <div>
              <RadioGroup
                label="Role"
                value={selectedRole}
                onValueChange={value => setValue('role', value as 'admin' | 'member')}
                isInvalid={!!errors.role}
                errorMessage={errors.role?.message}
                size="sm"
                orientation="horizontal"
              >
                <Radio
                  value="member"
                  description="Can view organization and participate in teams"
                  classNames={{ labelWrapper: 'ml-2' }}
                >
                  Member
                </Radio>
                <Radio
                  value="admin"
                  description="Can manage members, teams, and organization settings"
                  classNames={{ labelWrapper: 'ml-2' }}
                >
                  Admin
                </Radio>
              </RadioGroup>
            </div>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button variant="light" onPress={handleClose} isDisabled={inviteMember.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              isDisabled={inviteMember.isPending || !isDirty}
              data-testid="invite-member-submit"
            >
              {inviteMember.isPending ? <LoadingSpinner /> : 'Send Invitation'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
